import { Link, useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { Search, User, LogIn, LogOut, UserPlus, DollarSign, Home } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import { useContext, useState } from "react";
import Avatar from "@mui/material/Avatar";
import { VERSION } from "../utils/constants";

const Navbar = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // eslint-disable-next-line no-unused-vars
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    const first = firstName ? firstName[0] : '';
    const last = lastName ? lastName[0] : '';
    return (first + last).toUpperCase();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Get the token before clearing it
      const token = localStorage.getItem('token');
      
      // Make logout API call to auth service
      const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL;
      const authNextUrl = process.env.REACT_APP_SELF_URL;
      if (authBaseUrl && token) {
        const response = await fetch(`${authBaseUrl}/logout/?next=${authNextUrl}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        // Wait for the response to complete
        if (!response.ok) {
          console.error('Logout response error:', response.status);
        }
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear token from localStorage
      localStorage.removeItem('token');
      // Close menu
      handleMenuClose();
      // Redirect to opportunities page
      navigate('/opportunities');
      // Reload to reset auth state
      window.location.reload();
    }
  };

  const handleLogin = () => {
    const currentPath = location.pathname + location.search;
    navigate(`/auth/sign-in?redirect=${encodeURIComponent(currentPath)}`);
  };

  // Navigation items - all shown regardless of login status
  const getNavigationItems = () => {
    const items = [
      { 
        label: user ? 'Dashboard' : 'Home', 
        path: user ? '/dashboard' : '/', 
        icon: Home, 
        show: true, 
        disabled: false 
      },
      { label: 'Explore', path: '/opportunities', icon: Search, show: true, disabled: false },
      { label: 'Referrals', path: '/referrals', icon: UserPlus, show: true, disabled: true },
      { label: 'Earnings', path: '/earnings', icon: DollarSign, show: true, disabled: true },
      { label: 'Profile', path: '/profile', icon: User, show: true, disabled: !user }
    ];

    return items.filter(item => item.show);
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop Left Sidebar */}
      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            width: "80px",
            backgroundColor: "#ffffff",
            borderRight: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 3,
            zIndex: 1200,
            boxShadow: "2px 0 8px rgba(0,0,0,0.05)",
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <Tooltip title="Main Site" placement="right" arrow>
              <IconButton
                component="a"
                href={process.env.REACT_APP_HOME_URL || '/'}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Box
                  component="img"
                  src="/priyo-logo-small.jpg"
                  alt="Logo"
                  sx={{ 
                    height: 50,
                    width: 50,
                    objectFit: "contain"
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Navigation Icons */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = isActiveRoute(item.path);
              
              return (
                <Tooltip key={item.path} title={item.label} placement="right" arrow>
                  <span>
                    <IconButton
                      component={item.disabled ? 'div' : Link}
                      to={item.disabled ? undefined : item.path}
                      disabled={item.disabled}
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        backgroundColor: isActive ? "#3b82f6" : "transparent",
                        color: item.disabled ? "#cbd5e1" : (isActive ? "#ffffff" : "#64748b"),
                        transition: "all 0.2s",
                        cursor: item.disabled ? "not-allowed" : "pointer",
                        "&:hover": {
                          backgroundColor: item.disabled ? "transparent" : (isActive ? "#2563eb" : "#f1f5f9"),
                          color: item.disabled ? "#cbd5e1" : (isActive ? "#ffffff" : "#0f172a"),
                        },
                        "&.Mui-disabled": {
                          color: "#cbd5e1",
                        },
                      }}
                    >
                      <IconComponent size={24} />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
          </Box>

          {/* User Section at Bottom */}
          <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            {!user && (
              <Tooltip title="Log In" placement="right" arrow>
                <IconButton
                  onClick={handleLogin}
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    backgroundColor: "#3b82f6",
                    color: "#ffffff",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#2563eb",
                    },
                  }}
                >
                  <LogIn size={24} />
                </IconButton>
              </Tooltip>
            )}

            {user && (
              <Tooltip title="Account" placement="right" arrow>
                <Avatar
                  src={user?.profile_picture_url}
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: 'primary.main',
                    cursor: 'pointer',
                  }}
                  onClick={handleMenuOpen}
                >
                  {!user?.profile_picture_url && getInitials(user?.first_name, user?.last_name)}
                </Avatar>
              </Tooltip>
            )}

            {/* Version Display */}
            <Typography
              variant="caption"
              sx={{
                color: '#94a3b8',
                fontSize: '0.65rem',
                fontWeight: 500,
                letterSpacing: '0.5px',
              }}
            >
              {VERSION.full}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Mobile Top Bar */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "64px",
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            zIndex: 1200,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          {/* Logo */}
          <IconButton
            component="a"
            href={process.env.REACT_APP_HOME_URL || '/'}
            sx={{
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <Box
              component="img"
              src="/priyo-logo-small.jpg"
              alt="Logo"
              sx={{ 
                height: 40,
                width: 40,
                objectFit: "contain"
              }}
            />
          </IconButton>

          {/* Right side - User and Navigation Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = isActiveRoute(item.path);
              
              return (
                <IconButton
                  key={item.path}
                  component={item.disabled ? 'div' : Link}
                  to={item.disabled ? undefined : item.path}
                  disabled={item.disabled}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    backgroundColor: isActive ? "#3b82f6" : "transparent",
                    color: item.disabled ? "#cbd5e1" : (isActive ? "#ffffff" : "#64748b"),
                    transition: "all 0.2s",
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    "&:hover": {
                      backgroundColor: item.disabled ? "transparent" : (isActive ? "#2563eb" : "#f1f5f9"),
                      color: item.disabled ? "#cbd5e1" : (isActive ? "#ffffff" : "#0f172a"),
                    },
                    "&.Mui-disabled": {
                      color: "#cbd5e1",
                    },
                  }}
                >
                  <IconComponent size={20} />
                </IconButton>
              );
            })}

            {!user && (
              <IconButton
                onClick={handleLogin}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  backgroundColor: "#3b82f6",
                  color: "#ffffff",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                }}
              >
                <LogIn size={20} />
              </IconButton>
            )}

            {user && (
              <Avatar
                src={user?.profile_picture_url}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  cursor: 'pointer',
                }}
                onClick={handleMenuOpen}
              >
                {!user?.profile_picture_url && getInitials(user?.first_name, user?.last_name)}
              </Avatar>
            )}
          </Box>
        </Box>
      )}

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            minWidth: 200,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <User size={20} />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogOut size={20} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navbar;
